import type { Node, Parent } from 'unist';

import type { PullRequestReferenceOptions } from './pull-request-references';

import { buildPullRequestUrl, replacePullRequestReferences } from './pull-request-references';

const NON_LINKIFIABLE_ELEMENT_NAMES = new Set([
  'a',
  'code',
  'iframe',
  'noembed',
  'noframes',
  'plaintext',
  'pre',
  'script',
  'style',
  'textarea',
  'title',
  'xmp',
]);

type ElementNode = Parent & {
  properties: Record<string, unknown>;
  tagName: string;
  type: 'element';
};

type TextNode = Node & {
  type: 'text';
  value: string;
};

function hasChildren(node: Node): node is Parent {
  return 'children' in node && Array.isArray(node.children);
}

function linkifyTextNode(node: TextNode, options: PullRequestReferenceOptions): Node[] | null {
  return replacePullRequestReferences<Node>(
    node.value,
    options,
    (value) => ({ type: 'text', value }) as TextNode,
    (match) =>
      ({
        type: 'element',
        tagName: 'a',
        properties: {
          href: buildPullRequestUrl(match.options, match.number),
        },
        children: [{ type: 'text', value: match.reference } as TextNode],
      }) as ElementNode,
  );
}

function linkifyPullRequestReferences(parent: Parent, options: PullRequestReferenceOptions): void {
  for (let index = 0; index < parent.children.length; index += 1) {
    const child = parent.children[index];
    if (!child) {
      continue;
    }

    if (child.type === 'element') {
      const element = child as ElementNode;
      if (NON_LINKIFIABLE_ELEMENT_NAMES.has(element.tagName)) {
        continue;
      }
    }

    if (child.type === 'text') {
      const replacements = linkifyTextNode(child as TextNode, options);
      if (replacements) {
        parent.children.splice(index, 1, ...replacements);
        index += replacements.length - 1;
      }
      continue;
    }

    if (hasChildren(child)) {
      linkifyPullRequestReferences(child, options);
    }
  }
}

export default function rehypePullRequestReferences(options: PullRequestReferenceOptions) {
  return (tree: Parent): void => {
    linkifyPullRequestReferences(tree, options);
  };
}
